import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import useThemeColor from "../../hooks/useThemeColor.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/CustomComponents/Switchery/ToggleSwitch.css";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar.jsx";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState.jsx";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader.jsx";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader.js";

const emptyForm = {
  id: 0,
  code: "DOJO",
  name: "Dojo",
  defaultHostURL: "https://api.dojo.tech",
  apiVersion: "2024-02-05",
  softwareHouseId: "",
  resellerId: "",
  refundsEnabled: true,
  active: true,
  sortOrder: 1,
};

const ManagePaymentGatewayPlatforms = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState({ value: "SortOrder", label: "Sort Order" });
  const [sortOrder, setSortOrder] = useState({ value: "ASC", label: "Asc" });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const isEdit = form.id > 0;
  const isDojo = (form.code || "").toUpperCase() === "DOJO";

  const fetchPlatforms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.SA_GET_ALL_PAYMENT_GATEWAY_PLATFORMS, {
        params: {
          Search: search.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
        },
      });
      if (response.data.status) {
        setPlatforms(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        showErrorToast(response.data.message || "Failed to fetch payment gateway platforms");
        setPlatforms([]);
      }
    } catch (err) {
      const status = err.response?.status;
      const apiMessage = err.response?.data?.message || err.message;
      showErrorToast(
        status
          ? `Failed to fetch payment gateway platforms (${status}${apiMessage ? `: ${apiMessage}` : ""})`
          : `Failed to fetch payment gateway platforms${apiMessage ? `: ${apiMessage}` : ""}`
      );
      setPlatforms([]);
    } finally {
      setLoading(false);
    }
  }, [search, sortBy, sortOrder]);

  useEffect(() => {
    fetchPlatforms();
  }, [fetchPlatforms]);

  const handleSort = (field) => {
    if (sortBy.value === field) {
      setSortOrder((prev) => ({
        value: prev.value === "ASC" ? "DESC" : "ASC",
        label: prev.value === "ASC" ? "Desc" : "Asc",
      }));
      return;
    }
    setSortBy({ value: field, label: field });
    setSortOrder({ value: "ASC", label: "Asc" });
  };

  const openAdd = () => {
    setForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (platform) => {
    setForm({
      id: platform.id,
      code: platform.code || "",
      name: platform.name || "",
      defaultHostURL: platform.defaultHostURL || "",
      apiVersion: platform.apiVersion || "",
      softwareHouseId: platform.softwareHouseId || "",
      resellerId: platform.resellerId || "",
      refundsEnabled: platform.refundsEnabled !== false,
      active: platform.active !== false,
      sortOrder: platform.sortOrder || 1,
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.code?.trim()) {
      showErrorToast("Code is required");
      return;
    }
    if (!form.name?.trim()) {
      showErrorToast("Name is required");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ID: form.id || 0,
        Code: form.code.trim().toUpperCase(),
        Name: form.name.trim(),
        DefaultHostURL: form.defaultHostURL?.trim() || "",
        ApiVersion: form.apiVersion?.trim() || "",
        SoftwareHouseId: form.softwareHouseId?.trim() || "",
        ResellerId: form.resellerId?.trim() || "",
        RefundsEnabled: form.refundsEnabled,
        Active: form.active,
        SortOrder: parseInt(form.sortOrder, 10) || 1,
      };
      const response = await axiosInstance.post(APIs.SA_ADD_UPDATE_PAYMENT_GATEWAY_PLATFORM, payload);
      if (response.data.status) {
        showSuccessToast(response.data.message || "Platform saved");
        setShowModal(false);
        fetchPlatforms();
      } else {
        showErrorToast(response.data.message || "Failed to save platform");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save platform");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await swal({
      title: "Delete platform?",
      text: "Restaurants using this platform must be unlinked first.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!confirmed) return;
    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.SA_DELETE_PAYMENT_GATEWAY_PLATFORM, {
        params: { ID: id },
      });
      if (response.data.status) {
        showSuccessToast("Platform deleted");
        fetchPlatforms();
      } else {
        showErrorToast(response.data.message || "Failed to delete platform");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to delete platform");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (platform) => {
    try {
      const response = await axiosInstance.patch(APIs.SA_UPDATE_PAYMENT_GATEWAY_PLATFORM_STATUS, null, {
        params: { ID: platform.id, Active: !platform.active },
      });
      if (response.data.status) {
        showSuccessToast("Status updated");
        fetchPlatforms();
      } else {
        showErrorToast(response.data.message || "Failed to update status");
      }
    } catch (err) {
      showErrorToast("Failed to update status");
    }
  };

  const labelColor = isDarkMode ? "#ffffff" : "#000000";
  const iconBtnBase = useMemo(
    () => ({
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "6px",
      backgroundColor: "transparent",
      transition: "all 0.2s ease",
    }),
    []
  );

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Payment Gateway Platforms</li>
          </ol>
          <h1 className="page-header mb-0">Payment Gateway Platforms</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={openAdd}
            style={{
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Platform
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title="Platforms"
          totalCount={platforms.length}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search platforms..."
          bodyFlush
        >
          <div className="px-3 pb-3">
            <GridSectionLoader loading={loading} minHeight="280px">
              {!loading && platforms.length === 0 && (
                <GridEmptyState
                  isDarkMode={isDarkMode}
                  themeColor={themeColor}
                  title="No platforms found"
                  description={
                    search.trim()
                      ? "No platforms match your search."
                      : "Add a payment gateway platform such as Dojo."
                  }
                />
              )}

              {platforms.length > 0 && (
                <div className="table-responsive table-container">
                  <table
                    className="table table-hover align-middle"
                    style={{ "--modern-grid-sort-active": themeColor }}
                  >
                    <thead>
                      <tr>
                        <SortableColumnHeader sortField="Code" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                          Code
                        </SortableColumnHeader>
                        <SortableColumnHeader sortField="Name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                          Name
                        </SortableColumnHeader>
                        <th>Host URL</th>
                        <th className="text-center">Refunds</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {platforms.map((platform) => (
                        <tr key={platform.id}>
                          <td>{platform.code}</td>
                          <td>{platform.name}</td>
                          <td>{platform.defaultHostURL || "—"}</td>
                          <td className="text-center">{platform.refundsEnabled !== false ? "On" : "Off"}</td>
                          <td className="text-center">
                            <StatusBadge
                              isActive={platform.active}
                              onClick={() => handleToggleStatus(platform)}
                            />
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <button
                                onClick={() => openEdit(platform)}
                                className="btn btn-sm"
                                title="Edit platform"
                                style={{
                                  ...iconBtnBase,
                                  border: `1px solid ${themeColor}`,
                                  color: themeColor,
                                }}
                              >
                                <i className="fa fa-edit" style={{ fontSize: "12px" }}></i>
                              </button>
                              <button
                                onClick={() => handleDelete(platform.id)}
                                className="btn btn-sm"
                                title="Delete platform"
                                style={{
                                  ...iconBtnBase,
                                  border: "1px solid #dc3545",
                                  color: "#dc3545",
                                }}
                              >
                                <i className="fa fa-trash" style={{ fontSize: "12px" }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </GridSectionLoader>
          </div>
        </ModernGridToolbar>
      </div>

      <CustomModal
        title={isEdit ? "Update Platform" : "Add Platform"}
        subtitle="Platform settings apply to every restaurant that selects this gateway."
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSave}
        showButton={true}
        isCentered={true}
        modalSize="modal-lg"
        submitButtonText={isEdit ? "Update" : "Add"}
      >
        <div className="row g-3">
          <div className="col-md-6">
            <FormInput
              label="Code"
              name="code"
              type="text"
              value={form.code}
              placeHolder="DOJO"
              onchange={handleInputChange}
              readOnly={isEdit}
            />
            {isEdit ? (
              <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>Code cannot be changed after create.</small>
            ) : null}
          </div>
          <div className="col-md-6">
            <FormInput
              label="Name"
              name="name"
              type="text"
              value={form.name}
              placeHolder="Dojo"
              onchange={handleInputChange}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Default Host URL"
              name="defaultHostURL"
              type="text"
              value={form.defaultHostURL}
              placeHolder="https://api.dojo.tech"
              onchange={handleInputChange}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="API Version"
              name="apiVersion"
              type="text"
              value={form.apiVersion}
              placeHolder="2024-02-05"
              onchange={handleInputChange}
            />
          </div>
          {isDojo ? (
            <>
              <div className="col-md-6">
                <FormInput
                  label="Software House ID"
                  name="softwareHouseId"
                  type="text"
                  value={form.softwareHouseId}
                  placeHolder="Assigned by Dojo"
                  onchange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Reseller ID"
                  name="resellerId"
                  type="text"
                  value={form.resellerId}
                  placeHolder="Assigned by Dojo"
                  onchange={handleInputChange}
                />
              </div>
            </>
          ) : null}
          <div className="col-md-6">
            <label className="form-label" style={{ color: labelColor }}>
              Gateway refunds
            </label>
            <ToggleSwitch
              isChecked={form.refundsEnabled}
              onToggle={() => setForm((prev) => ({ ...prev, refundsEnabled: !prev.refundsEnabled }))}
              label={form.refundsEnabled ? "Enabled" : "Disabled"}
            />
            <small className="d-block mt-1" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
              Uses each restaurant Primary Key. No separate refund secret.
            </small>
          </div>
          <div className="col-md-6">
            <label className="form-label" style={{ color: labelColor }}>
              Status
            </label>
            <ToggleSwitch
              isChecked={form.active}
              onToggle={() => setForm((prev) => ({ ...prev, active: !prev.active }))}
              label={form.active ? "Active" : "Inactive"}
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default ManagePaymentGatewayPlatforms;
